import { defineStore } from 'pinia'

/**
 * Ag Grid store.
 *
 * @author Alejandro Sanchez <asanchez@boltsystem.com>
 */
export const useAgGridStore = defineStore('agGrid', {
    /**
     * Ag Grid's state.
     *
     * @type  {Object}
     */
    state: () => ({
        filters: [],
        active: {
            view: null,
        },
        views: [],
        viewDefaults: [],
    }),
    /**
     * Ag Grid's getters.
     *
     * @type  {Object}
     */
    getters: {
        /**
         * Get the active view.
         *
         * @param {Object} state
         * @return {Object}
         */
        view: (state) => {
            if (!state.active.view) {
                return null
            }

            return state.views.find((view) => view.name === state.active.view)
        },
    },

    /**
     * Ag Grid's actions.
     *
     * @type {Object}
     */
    actions: {
        /**
         * Add a new filter.
         *
         * @param {Object} filter
         * @return {void}
         */
        addFilter(filter) {
            const index = this.filters.find((item) => item.key === filter.key)

            if (index) {
                return
            }

            this.filters.push(filter)
        },

        /**
         * Clear all filters.
         *
         * @return {void}
         */
        clearFilters() {
            this.filters = []
        },

        /**
         * Remove a filter.
         *
         * @param {String} key
         * @return {void}
         */
        removeFilter(key) {
            this.filters = this.filters.filter((item) => item.key !== key)
        },

        /**
         * Reset the view's filters back to the default.
         *
         * @return {void}
         */
        resetDefaultFilters() {
            const view = this.views.find(
                (view) => view.name === this.active.view
            )

            const defaultView = this.viewDefaults.find(
                (view) => view.name === this.active.view
            )

            if (view && defaultView) {
                view.filterState = { ...defaultView.filterState }
            }
        },

        /**
         * Set the active view.
         *
         * @param {Object} view
         * @return {void}
         */
        setActiveView(view) {
            this.active.view = view || null
        },

        /**
         * Set the views.
         *
         * @param {Array} views
         * @return {void}
         */
        setViews(views = []) {
            this.views = [...(views || [])].map((view) => ({ ...view }))
            this.viewDefaults = [...this.views].map((view) => ({ ...view }))
        },

        /**
         * Set the view filters.
         *
         * @param {Object} filterState
         */
        setViewFilters(filterState) {
            const view = this.views.find(
                (view) => view.name === this.active.view
            )

            if (!view) {
                return
            }

            view.filterState = { ...filterState }
        },
    },
})
